"use client";

import ProfilePicture from "@/components/common/profile-picture";
import VerifiedBadgeCheck from "@/components/common/verified-badge-check";
import { POSTS_API } from "@/lib/apis";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { TPostComment } from "@/types/post";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceStrict } from "date-fns";
import {
	ClockIcon,
	CornerDownRightIcon,
	Loader2Icon,
	MessageSquareIcon,
	TrashIcon,
} from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import toast from "react-hot-toast";
import PostCommentLikeButton from "./comment-like-button";
import CommentMediaGrid from "./comment-media-grid";
import CustomEditorProvider from "@/providers/custom-editor-provider";
import PostCommentInputField from "./comment-input-field";
import cn from "@/lib/cn";
import { JSONContentRenderer } from "@/components/common/editors/json-renderer";
import { getEditorConfig } from "@/components/common/editors/base-editor/base-editor";

type PostCommentCardProps = {
	isReply?: boolean;
	postSlug: string;
	comment: TPostComment;
	showReplyInput?: boolean;
	selectedCommentId?: string;
	toggleReply: (commentId: string) => void;
};

const PostCommentCard = ({
	postSlug,
	comment,
	showReplyInput,
	selectedCommentId,
	toggleReply,
	isReply,
}: PostCommentCardProps) => {
	const { user, isLoggedIn } = useAuth();
	const isAdmin = user?.isAdmin;
	const username = user?.username;

	const queryClient = useQueryClient();
	const { openModal } = useModal();

	// TODO: Fix this to use IDs instead of username
	const isCreator = comment.creator.username === username;

	const [showMoreReplies, setShowMoreReplies] = useState<boolean>(true);

	const handleDeleteComment = async () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);

			return;
		}

		const url = new URL(`${POSTS_API}/${postSlug}/comments`);

		await fetch(url, {
			method: "DELETE",
			body: JSON.stringify({
				commentSlug: comment.slug,
			}),
		});
	};

	const deleteCommentMutation = useMutation<void, Error>({
		mutationFn: handleDeleteComment,
		onSuccess: () => {
			toast.success("Comment deleted successfully");
			return queryClient.invalidateQueries({ queryKey: ["post-comments", postSlug] });
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	const handleShowMoreRepliesClick = () => {
		setShowMoreReplies((prev) => !prev);
	};

	const commentImages = comment?.media;

	const replies = comment?.replies || [];

	const slicedReplies = showMoreReplies ? replies : replies.slice(0, 1);

	return (
		<div key={comment.id} id={`comment-${comment.slug}`} className="w-full">
			<div className="rounded-lg border bg-brand-primary/[0.5%] p-3">
				<div className="flex items-center justify-between">
					{/* Profile Picture, Name, Role, Company */}
					<div className="flex items-center justify-start gap-3">
						<Link href={comment?.creator.username ? `/${comment?.creator.username}` : `#`}>
							<ProfilePicture
								profilePictureUrl={comment.creator.image}
								alternateText={comment.creator.name}
								className={{
									container: "h-10 w-10 rounded-full p-0.5",
									image: "rounded-full text-gray-600",
									badgeImage: "-bottom-3 h-7 w-7",
								}}
								isVerified={comment.creator.isVerified}
								isFounder={comment.creator.isFounder}
							/>
						</Link>

						{/* Name, Role, Company */}
						<div className="flex flex-col items-start">
							<div className="flex items-center gap-2">
								<Link
									href={comment?.creator.username ? `/${comment?.creator.username}` : `#`}
									className="font-bold capitalize text-dark-primary md:text-lg"
								>
									{comment?.creator?.name}
								</Link>

								{comment.creator.isFounder && comment.creator.isVerified && (
									<VerifiedBadgeCheck iconClassName="w-5 h-5" />
								)}
							</div>
							<p className="text-xs text-gray-600">
								{comment?.creator?.jobTitle}
								{comment?.creator?.company ? `, ${comment?.creator?.company}` : ""}
							</p>
						</div>
					</div>

					{/* Delete Comment Button */}
					{(isCreator || isAdmin) && (
						<button
							type="button"
							onClick={() => deleteCommentMutation.mutate()}
							className="rounded-md border p-2 transition-colors duration-200 ease-in-out hover:bg-red-500 hover:bg-opacity-10 disabled:cursor-not-allowed disabled:opacity-60"
							disabled={deleteCommentMutation.isLoading}
						>
							{deleteCommentMutation.isLoading ? (
								<Loader2Icon className="h-4 w-4 animate-spin text-brand-primary" />
							) : (
								<TrashIcon className="h-4 w-4" />
							)}

							<span className="sr-only">Delete Comment</span>
						</button>
					)}
				</div>

				{/* Content */}
				<div className="mt-4">
					{comment.version === "v2" ? (
						<JSONContentRenderer
							className="whitespace-pre-wrap text-gray-700"
							json={comment.jsonContent || {}}
							extensions={getEditorConfig({ type: "comment" }).extensions}
						/>
					) : (
						<div
							className="whitespace-pre-wrap text-gray-700"
							dangerouslySetInnerHTML={{ __html: comment.content }}
						/>
					)}
				</div>

				{/* Media */}
				<div className="mt-4">
					<CommentMediaGrid items={commentImages} />
				</div>

				{/* Like Button, Reply Button, Timestamp */}
				<div className="mt-2 flex items-center justify-end gap-4 max-md:flex-wrap">
					<PostCommentLikeButton
						postSlug={postSlug}
						commentSlug={comment.slug}
						likesCount={comment.likesCount}
						isLiked={comment.isLikedByUser}
					/>
					{!isReply && (
						<button
							type="button"
							onClick={() => toggleReply(comment.id)}
							className={cn(
								"flex items-center justify-start gap-1 rounded-md border px-2 py-1 text-xs",
								comment.id === selectedCommentId
									? "border-brand-primary text-brand-primary"
									: "border-gray-500 text-gray-500"
							)}
						>
							<MessageSquareIcon className="h-4 w-4" />
							<span className="font-medium">Reply</span>
						</button>
					)}

					<div className="flex items-center justify-start gap-1 rounded-md border px-2 py-1 text-xs text-gray-500">
						<ClockIcon className="h-4 w-4" />
						<span>{formatDistanceStrict(new Date(comment.updatedAt), new Date())}</span>
					</div>
				</div>
			</div>

			{/* Reply Input Field - Temporarily disabled */}
			{showReplyInput && comment.id === selectedCommentId && (
				<div className="mt-2 flex items-center justify-start gap-2">
					<CornerDownRightIcon className="h-5 w-5 text-gray-400" />

					<div className="basis-full">
						<CustomEditorProvider defaultValue={{ editorType: "comment" }}>
							<PostCommentInputField
								postSlug={postSlug}
								parentCommentId={comment.id}
								onSubmit={() => console.log("submit")}
							/>
						</CustomEditorProvider>
					</div>
				</div>
			)}

			{/* Replies */}
			{slicedReplies && slicedReplies.length > 0 && (
				<div className="mt-2 space-y-2">
					{slicedReplies.map((reply) => (
						<div key={reply.id} className="flex items-start justify-start gap-2">
							<CornerDownRightIcon className="h-5 w-5 text-gray-400" />

							<PostCommentCard
								isReply={true}
								postSlug={postSlug}
								comment={reply}
								toggleReply={toggleReply}
								showReplyInput={showReplyInput}
								selectedCommentId={selectedCommentId}
							/>
						</div>
					))}

					{replies?.length > 1 && (
						<button
							type="button"
							className="mb-4 ml-7 text-sm font-semibold text-brand-primary"
							onClick={handleShowMoreRepliesClick}
						>
							{showMoreReplies ? "Hide replies" : "Show more replies"}
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default PostCommentCard;
